/**
 * Turns a company's stored industry code into what the company page shows: the
 * WZ class in dotted notation, its name, and the name of its division.
 *
 * The stored code is the section letter plus Creditreform's digits (`C25620`,
 * sometimes shorter, e.g. `C10`). The page shows the 4-digit CLASS (`25.62`), so
 * the code and the name always describe the same level: a subclass such as
 * `G46184` reads as class `46.18` with the class name, not the subclass name.
 *
 * Names come from the industry-code tree the search form already loads
 * (`options.industryCodes.flatList`). The backend only adds a level to that tree
 * when its master list has a name for it (see `nomenclature.js`), and the master
 * before TD-3226 leaves out a level that is the only child of its parent and shares
 * its name: class 64.20 is missing because group 64.2 holds nothing else and is also
 * called "Activities of holding companies". A skipped class can still have subclasses
 * in the tree, hung directly under the group: 70.10 is missing, but M701 holds M70101
 * and M70109. So when the class has no node, the nearest ancestor lends its NAME only
 * if every child it has in the tree belongs to this class: then the class was its only
 * child and carries the same name. An ancestor with children outside the class names
 * a broader category, so the code then stands alone. The code shown always stays the
 * class. Node names carry their own code as a prefix ("C2562. Machining"), which is
 * stripped.
 *
 * A class can carry the same name as its division (12.00 "Manufacture of tobacco
 * products"). The division name is then left out, so the page does not say it twice.
 *
 * Temporary: TD-3226 completes the master list. Once that is live everywhere, every
 * class has its own node and the ancestor fallback in `findNamingNode` can go.
 */

// Section letter + 2 digits: the shortest code that names a division.
const DIVISION_LENGTH = 3;
// Section letter + 4 digits.
const CLASS_LENGTH = 5;
// Digits that make up a class, for a code stored without its section letter.
const CLASS_DIGITS = CLASS_LENGTH - 1;

// The options object is loaded once per session, so the index is built once per tree.
const treeIndexByList = new WeakMap();

const buildTreeIndex = (flatList) => {
  const childValuesByParent = new Map();
  flatList.forEach((node) => {
    const parentValue = node.parent?.value;
    if (!parentValue) {
      return;
    }
    if (!childValuesByParent.has(parentValue)) {
      childValuesByParent.set(parentValue, []);
    }
    childValuesByParent.get(parentValue).push(node.value);
  });
  return { nodes: new Map(flatList.map((node) => [node.value, node])), childValuesByParent };
};

const getTreeIndex = (flatList) => {
  if (!Array.isArray(flatList)) {
    return null;
  }
  if (!treeIndexByList.has(flatList)) {
    treeIndexByList.set(flatList, buildTreeIndex(flatList));
  }
  return treeIndexByList.get(flatList);
};

const nodeName = (node) => {
  const prefix = `${node.value}. `;
  return node.name?.startsWith(prefix) ? node.name.slice(prefix.length) : node.name || null;
};

// WZ notation puts a dot after the 2-digit division: '2562' -> '25.62', '256' -> '25.6'.
const formatWzDigits = (digits) =>
  digits.length > 2 ? `${digits.slice(0, 2)}.${digits.slice(2)}` : digits;

// The class's own node, or the nearest ancestor that stands in for it (see above).
const findNamingNode = ({ nodes, childValuesByParent }, classCode) => {
  if (nodes.has(classCode)) {
    return nodes.get(classCode);
  }
  for (let length = classCode.length - 1; length >= DIVISION_LENGTH; length -= 1) {
    const ancestor = nodes.get(classCode.slice(0, length));
    if (ancestor) {
      const childValues = childValuesByParent.get(ancestor.value) || [];
      return childValues.every((value) => value.startsWith(classCode)) ? ancestor : null;
    }
  }
  return null;
};

/**
 * @param {string} industryCode - stored code, e.g. 'C25620'
 * @param {Array<{value: string, name: string, parent?: {value: string}}>} [flatList] -
 *   flat industry-code tree; absent until the options have loaded
 * @returns {{code: string, name: string|null, divisionName: string|null}|null}
 *   null when the company has no code. Without a node that names the class, `name`
 *   is null and `code` is the stored class. `divisionName` is null when it would
 *   repeat `name`.
 */
export const resolveIndustryCode = (industryCode, flatList) => {
  if (typeof industryCode !== 'string') {
    return null;
  }

  const stored = industryCode.trim().toUpperCase();
  // Without the section letter there is nothing to look up, but the digits are still the code.
  if (/^\d/.test(stored)) {
    return { code: formatWzDigits(stored.slice(0, CLASS_DIGITS)), name: null, divisionName: null };
  }

  const classCode = stored.slice(0, CLASS_LENGTH);
  if (classCode.length < DIVISION_LENGTH) {
    return null;
  }

  const code = formatWzDigits(classCode.slice(1));
  const index = getTreeIndex(flatList);
  const node = index && findNamingNode(index, classCode);
  if (!node) {
    return { code, name: null, divisionName: null };
  }

  const name = nodeName(node);
  const divisionNode = index.nodes.get(classCode.slice(0, DIVISION_LENGTH));
  const divisionName = divisionNode ? nodeName(divisionNode) : null;
  return { code, name, divisionName: divisionName === name ? null : divisionName };
};
